import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { Brain, Target, Sparkles, TrendingUp, Trophy, BookOpen, type LucideIcon } from 'lucide-react';

/**
 * Branded full-screen loading experience.
 *
 * - `quiz`: anticipation sequence while the adaptive engine prepares a
 *   session — icons pulse through the "thinking" steps with rotating,
 *   product-specific status copy.
 * - `dashboard`: lightweight onboarding while data loads — rotates the
 *   product's value propositions so the wait teaches new users what
 *   they're getting.
 *
 * Mechanics:
 * - Renders nothing for the first 250ms: fast loads (the common case
 *   since the backend optimizations) never flash a loader at all.
 * - Respects prefers-reduced-motion: static icon + first message.
 * - role="status" + aria-live for screen readers.
 */

interface LoaderStep {
  icon: LucideIcon;
  title: string;
  caption: string;
}

const QUIZ_STEPS: LoaderStep[] = [
  {
    icon: Brain,
    title: 'Reviewing your history',
    caption: 'Your past answers shape what comes next'
  },
  {
    icon: Target,
    title: 'Calibrating difficulty',
    caption: 'Matching questions to your current level'
  },
  {
    icon: Sparkles,
    title: 'Selecting your questions',
    caption: 'Weighing the areas that need you most'
  }
];

const DASHBOARD_STEPS: LoaderStep[] = [
  {
    icon: Brain,
    title: 'Adaptive practice',
    caption: 'Every question is chosen for your level. No two sessions alike'
  },
  {
    icon: TrendingUp,
    title: 'Progress that compounds',
    caption: 'Your strengths and gaps tracked across every ASWB content area'
  },
  {
    icon: Trophy,
    title: 'Exam-day readiness',
    caption: 'Build the score, and the confidence, to pass the first time'
  },
  {
    icon: BookOpen,
    title: 'Study what matters',
    caption: 'Missed questions become flashcards, automatically'
  }
];

interface BrandedLoaderProps {
  variant: 'quiz' | 'dashboard';
  /** ms before the loader becomes visible (fast loads never flash) */
  appearDelayMs?: number;
  /** ms per rotating step */
  stepIntervalMs?: number;
}

export const BrandedLoader: React.FC<BrandedLoaderProps> = ({
  variant,
  appearDelayMs = 250,
  stepIntervalMs = 2200
}) => {
  const steps = variant === 'quiz' ? QUIZ_STEPS : DASHBOARD_STEPS;
  const heading = variant === 'quiz' ? 'Preparing your session' : 'Welcome back';
  const reducedMotion = useReducedMotion();

  const [visible, setVisible] = useState(appearDelayMs === 0);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (visible) return;
    const t = setTimeout(() => setVisible(true), appearDelayMs);
    return () => clearTimeout(t);
  }, [visible, appearDelayMs]);

  useEffect(() => {
    if (!visible || reducedMotion) return;
    const t = setInterval(
      () => setStepIndex((i) => (i + 1) % steps.length),
      stepIntervalMs
    );
    return () => clearInterval(t);
  }, [visible, reducedMotion, steps.length, stepIntervalMs]);

  const step = steps[stepIndex];
  const Icon = step.icon;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="branded-loader"
      className="flex items-center justify-center min-h-screen bg-neutral-50 px-6"
    >
      <span className="sr-only">Loading…</span>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="text-center max-w-sm w-full"
          >
            {/* Icon stage: soft pulsing brand ring with crossfading icon */}
            <div className="relative w-24 h-24 mx-auto mb-8">
              {!reducedMotion && (
                <>
                  <motion.div
                    className="absolute inset-0 rounded-full bg-primary-100"
                    animate={{ scale: [1, 1.18, 1], opacity: [0.7, 0.35, 0.7] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <motion.div
                    className="absolute inset-2 rounded-full bg-primary-200/60"
                    animate={{ scale: [1, 1.1, 1], opacity: [0.8, 0.5, 0.8] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut', delay: 0.25 }}
                  />
                </>
              )}
              <div className="absolute inset-4 rounded-full bg-neutral-50 shadow-ledge flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={stepIndex}
                    initial={reducedMotion ? false : { opacity: 0, scale: 0.7, rotate: -8 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    exit={reducedMotion ? undefined : { opacity: 0, scale: 0.7, rotate: 8 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 24 }}
                    className="text-primary-600"
                  >
                    <Icon className="w-9 h-9" aria-hidden="true" />
                  </motion.span>
                </AnimatePresence>
              </div>
            </div>

            <h2 className="heading-3 mb-6">
              {heading}
            </h2>

            {/* Rotating step copy */}
            <div className="min-h-[3.5rem]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={stepIndex}
                  initial={reducedMotion ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reducedMotion ? undefined : { opacity: 0, y: -6 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  <p className="text-sm font-semibold text-primary-700">{step.title}</p>
                  <p className="text-sm text-neutral-600 mt-1">{step.caption}</p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Step dots */}
            <div className="flex items-center justify-center gap-2 mt-6" aria-hidden="true">
              {steps.map((_, i) => (
                <motion.span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-primary-300"
                  animate={{
                    scale: i === stepIndex ? 1.4 : 1,
                    opacity: i === stepIndex ? 1 : 0.45
                  }}
                  transition={{ duration: 0.3 }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BrandedLoader;
